import { Alert, Box, Snackbar, styled } from '@mui/material'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { formatTimestampToHumanReadable } from './date-utils'
import useFlags from './useFlags'

interface DowntimeConfig {
  startDate: string
  endDate: string
  additionalMessage?: string
}

const NotificationAlert = styled(Alert)(({ theme }) => ({
  width: '100vw',
  borderRadius: '0px',
  backgroundColor: `${theme.palette.primary.light} !important`,
  padding: '0px 16px',
  // Ensure all text is dark and readable
  color: `${theme.palette.text.primary} !important`,
  fontWeight: 500, // Medium weight for better readability
  // Ensure all text content (including nested divs, spans, etc.) is readable
  '& p, & div, & span, & strong, & b': {
    color: `${theme.palette.text.primary} !important`,
  },
  '& a': {
    color: `${theme.palette.primary.main} !important`,
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  '& .MuiAlert-message': {
    color: `${theme.palette.text.primary} !important`,
    fontWeight: 500,
    '& p, & div, & span': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  '& .MuiAlert-icon': {
    color: `${theme.palette.warning.main} !important`,
    fontSize: '1.25rem', // Slightly larger for better visibility
  },
  '& .MuiAlert-action': {
    '& .MuiIconButton-root': {
      color: `${theme.palette.text.primary} !important`,
      fontSize: '1.25rem', // Larger close button for better visibility
      '&:hover': {
        backgroundColor: 'rgba(30, 26, 69, 0.08)', // More visible hover state
      },
      '&:focus': {
        backgroundColor: 'rgba(30, 26, 69, 0.12)', // Visible focus state
      },
    },
  },
}))

const StyledSnackbar = styled(Snackbar)({
  '&.MuiSnackbar-anchorOriginBottomCenter': {
    bottom: '0px',
  },
  '&.MuiSnackbar-root': {
    top: '40px',
    left: '0px',
    display: 'block',
  },
})

export const DeepcellDowntimeAlert = (): JSX.Element => {
  const [open, setOpen] = useState(true)
  const { downtime } = useFlags()
  const [formattedMessage, setFormattedMessage] = useState<string>('')
  const [additionalMessage, setAdditionalMessage] = useState<string | undefined>(undefined)

  const attachToDOMNode = () => {
    if (document?.fullscreenElement) return document.fullscreenElement
    return document.body
  }

  useEffect(() => {
    if (!downtime || downtime.length === 0) {
      setFormattedMessage('')
      setAdditionalMessage(undefined)
      setOpen(false)
      return
    }

    try {
      // Try to parse as JSON first (new format)
      const parsed: DowntimeConfig = JSON.parse(downtime)
      if (parsed.startDate && parsed.endDate) {
        try {
          const startFormatted = formatTimestampToHumanReadable(
            parsed.startDate,
            'YYYY-MM-DDTHH:mm:ssZ'
          )
          const endFormatted = formatTimestampToHumanReadable(parsed.endDate, 'YYYY-MM-DDTHH:mm:ssZ')
          setFormattedMessage(`${startFormatted} - ${endFormatted}`)
          setAdditionalMessage(parsed.additionalMessage)
          setOpen(true)
          return
        } catch (error) {
          // Error converting timestamps, fall through to old format handling
        }
      }
    } catch (error) {
      // Not valid JSON, try old string format
    }

    // Fall back to old string format for backward compatibility
    if (downtime && downtime.length > 0) {
      const [startDowntimeStr, endDowntimeStr] = downtime.split(' - ')
      if (startDowntimeStr && endDowntimeStr) {
        try {
          const formatted = `${formatTimestampToHumanReadable(
            startDowntimeStr.trim(),
            'MM-DD-YYYY HH:mm'
          )} - ${formatTimestampToHumanReadable(endDowntimeStr.trim(), 'MM-DD-YYYY HH:mm')}`
          setFormattedMessage(formatted)
          setAdditionalMessage(undefined)
          setOpen(true)
          return
        } catch (error) {
          // error converting the timestamp specified in the firebase
        }
      }
    }

    setFormattedMessage('')
    setAdditionalMessage(undefined)
    setOpen(false)
  }, [downtime])

  const handleClose = () => {
    setOpen(false)
  }

  if (formattedMessage && formattedMessage.length > 0) {
    return createPortal(
      <StyledSnackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={open}
      >
        <Box>
          <NotificationAlert variant="filled" severity="warning" onClose={handleClose}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ marginRight: 1 }}>
                  Please be advised that there will be scheduled downtime across Axon:
                </Box>
                <Box sx={{ fontWeight: 600 }}>{formattedMessage}</Box>
              </Box>
              {additionalMessage && <Box sx={{ marginTop: 0.5 }}>{additionalMessage}</Box>}
            </Box>
          </NotificationAlert>
        </Box>
      </StyledSnackbar>,
      attachToDOMNode()
    )
  }
  return <></>
}

export default DeepcellDowntimeAlert
